import type { ReactNode } from "react"
import styled from "styled-components"
import { Link, NavLink, useNavigate } from "react-router-dom"
import SvgLogo from "../../../app/assets/img/logo.svg"
import useAuthStore from "@features/auth/authStore.client"
import InstallPortalButton from "@features/pwa/InstallPortalButton"
import { normalizePortalRole, roleLabel } from "@features/portal/types"
import NotificationMenu from "@features/portal/NotificationMenu"

const Shell = styled.div`
  min-height: 100vh;
  background: #edf2f6;
  color: #102840;
`

const Sidebar = styled.aside`
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 30;
  width: 270px;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  background: #071a2e;
  color: #ffffff;
  border-right: 1px solid rgba(154, 199, 232, 0.18);

  @media (max-width: 960px) {
    display: none;
  }
`

const Brand = styled(Link)`
  display: block;
  padding: 0.4rem 0 1.8rem;

  svg {
    width: 174px;
    height: auto;
    filter: brightness(0) invert(1);
  }
`

const SpaceLabel = styled.p`
  margin: 0 0 1.5rem;
  padding: 0.85rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  color: #86c8f0;
  font-size: 0.72rem;
  font-weight: 750;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`

const Nav = styled.nav`
  display: grid;
  gap: 0.3rem;

  a {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    min-height: 48px;
    padding: 0 0.85rem;
    border-left: 2px solid transparent;
    color: rgba(255, 255, 255, 0.72);
    text-decoration: none;
    font-size: 0.92rem;
    font-weight: 650;
  }

  a::before {
    content: "";
    width: 8px;
    height: 8px;
    border: 1px solid currentColor;
  }

  a:hover,
  a.active {
    border-left-color: #64b6e6;
    background: rgba(99, 195, 255, 0.09);
    color: #ffffff;
  }

  a.active::before {
    background: #64b6e6;
    border-color: #64b6e6;
  }
`

const SidebarFooter = styled.div`
  margin-top: auto;
  display: grid;
  gap: 0.65rem;
`

const HelpLink = styled.a`
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.85rem;
  text-decoration: none;

  strong {
    display: block;
    margin-top: 0.2rem;
    color: #ffffff;
    font-size: 1rem;
  }
`

const Main = styled.div`
  min-height: 100vh;
  margin-left: 270px;

  @media (max-width: 960px) {
    margin-left: 0;
    padding-bottom: 76px;
  }
`

const Topbar = styled.header`
  position: sticky;
  top: 0;
  z-index: 20;
  min-height: 78px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem clamp(1rem, 4vw, 3rem);
  background: rgba(255, 255, 255, 0.94);
  border-bottom: 1px solid #c8d4de;
  backdrop-filter: blur(14px);
`

const MobileBrand = styled(Link)`
  display: none;

  svg {
    width: 132px;
    height: auto;
  }

  @media (max-width: 960px) {
    display: block;
  }
`

const TopbarMeta = styled.div`
  display: grid;
  gap: 0.1rem;

  span {
    color: #6b7f91;
    font-size: 0.72rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  strong {
    font-size: 0.95rem;
  }

  @media (max-width: 960px) {
    display: none;
  }
`

const TopbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
`

const LogoutButton = styled.button`
  min-height: 42px;
  padding: 0 1rem;
  border: 1px solid #c0ceda;
  border-radius: 2px;
  background: #ffffff;
  color: #173a5d;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 750;
  cursor: pointer;

  @media (max-width: 520px) {
    padding: 0 0.65rem;
    font-size: 0.75rem;
  }
`

const Content = styled.main`
  width: min(1480px, 100%);
  margin: 0 auto;
  padding: clamp(1.5rem, 4vw, 3.5rem);
`

const PageHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  margin-bottom: clamp(2rem, 5vw, 3.5rem);
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #becbd6;

  @media (max-width: 700px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 1rem;
  }
`

const Eyebrow = styled.p`
  margin: 0 0 0.7rem;
  color: #337abd;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.17em;
  text-transform: uppercase;
`

const Title = styled.h1`
  margin: 0;
  max-width: 900px;
  color: #0b2c4a;
  font-size: clamp(2.35rem, 5vw, 4.8rem);
  font-weight: 580;
  line-height: 0.98;
  letter-spacing: -0.05em;
`

const MobileNav = styled.nav`
  display: none;

  @media (max-width: 960px) {
    position: fixed;
    z-index: 40;
    right: 0;
    bottom: 0;
    left: 0;
    height: 68px;
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    background: #071a2e;
    border-top: 1px solid rgba(154, 199, 232, 0.24);

    a {
      display: grid;
      place-items: center;
      padding: 0.5rem 0.25rem;
      color: rgba(255, 255, 255, 0.7);
      text-decoration: none;
      text-align: center;
      font-size: 0.68rem;
      font-weight: 700;
    }

    a.active {
      background: rgba(99, 195, 255, 0.12);
      color: #ffffff;
      box-shadow: inset 0 2px #63c3ff;
    }
  }
`

interface PortalShellProps {
  children: ReactNode
  title: string
  eyebrow?: string
  action?: ReactNode
}

const PortalShell = ({ children, title, eyebrow = "Espace TB", action }: PortalShellProps) => {
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const navigate = useNavigate()
  const role = normalizePortalRole(user?.role)
  const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.email || "Utilisateur"

  const navItems = [
    { to: "/app", label: "Vue d’ensemble", end: true, roles: ["LOCATAIRE", "PROPRIETAIRE", "ADMIN"] },
    { to: "/app/demandes", label: "Demandes", roles: ["LOCATAIRE", "PROPRIETAIRE", "ADMIN"] },
    { to: "/app/nouvelle-demande", label: "Nouvelle demande", roles: ["LOCATAIRE", "PROPRIETAIRE", "ADMIN"] },
    { to: "/app/rapports", label: "Rapports", roles: ["PROPRIETAIRE", "ADMIN"] },
    { to: "/app/reception", label: "Réception", roles: ["ADMIN"] },
    { to: "/app/acces", label: "Accès", roles: ["ADMIN"] },
    { to: "/app/gestion-des-candidatures", label: "Candidatures", roles: ["ADMIN"] },
    { to: "/app/gestion-des-proprietes", label: "Immeubles", roles: ["ADMIN"] },
    { to: "/app/aide", label: "Aide", roles: ["LOCATAIRE", "PROPRIETAIRE", "ADMIN"] },
  ].filter((item) => item.roles.includes(role))

  const mobileNavItems = [
    { to: "/app", label: "Accueil", end: true },
    { to: "/app/demandes", label: "Demandes" },
    ...(role === "ADMIN" ? [{ to: "/app/reception", label: "Réception" }] : [{ to: "/app/nouvelle-demande", label: "Nouvelle" }]),
    ...(role === "PROPRIETAIRE" ? [{ to: "/app/rapports", label: "Rapports" }] : []),
    ...(role === "ADMIN" ? [{ to: "/app/acces", label: "Accès" }] : []),
    { to: "/app/aide", label: "Aide" },
  ]

  const handleLogout = async () => {
    await logout().catch(() => undefined)
    navigate("/se-connecter")
  }

  return (
    <Shell>
      <Sidebar>
        <Brand to="/app" aria-label="Espace TB — accueil">
          <SvgLogo />
        </Brand>
        <SpaceLabel>{roleLabel[role]}</SpaceLabel>
        <Nav aria-label="Navigation de l’Espace TB">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}>
              {item.label}
            </NavLink>
          ))}
        </Nav>
        <SidebarFooter>
          <HelpLink href="tel:4186849004">
            Urgence locataire · 7 j/7
            <strong>(418) 684-9004</strong>
          </HelpLink>
        </SidebarFooter>
      </Sidebar>

      <Main>
        <Topbar>
          <MobileBrand to="/app" aria-label="Espace TB — accueil">
            <SvgLogo />
          </MobileBrand>
          <TopbarMeta>
            <span>{roleLabel[role]}</span>
            <strong>{fullName}</strong>
          </TopbarMeta>
          <TopbarActions>
            <NotificationMenu />
            <InstallPortalButton label="Installer" />
            <LogoutButton onClick={handleLogout}>Déconnexion</LogoutButton>
          </TopbarActions>
        </Topbar>

        <Content>
          <PageHeader>
            <div>
              <Eyebrow>{eyebrow}</Eyebrow>
              <Title>{title}</Title>
            </div>
            {action}
          </PageHeader>
          {children}
        </Content>
      </Main>

      <MobileNav aria-label="Navigation mobile de l’Espace TB">
        {mobileNavItems.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end}>
            {item.label}
          </NavLink>
        ))}
      </MobileNav>
    </Shell>
  )
}

export default PortalShell
